import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import Icon from '../../components/Icon'
import { Breadcrumb, Button, FormGroup, Select, StatusBadge } from '../../components/ui'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import {
  APPROVAL_STATUS_META,
  STATUS_APPROVED,
  STATUS_PENDING,
  STATUS_REJECTED,
  buildApprovalSummary,
  contractSubtitle,
  getApprovalStatusMeta,
  getApproverInitials,
  splitApprovalsByStatus,
} from './contract-approvals/approvalUtils'
import './Company.css'
import './contract-approvals/ContractApprovals.css'

function formatDate(d) {
  if (!d) return '—'
  try {
    const date = typeof d === 'string' ? new Date(d) : d
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (_) {
    return '—'
  }
}

function statusDate(approval) {
  const status = String(approval?.status || '').toLowerCase().trim()
  if (status === STATUS_APPROVED) return approval.approved_at || approval.updated_at
  if (status === STATUS_REJECTED) return approval.rejected_at || approval.updated_at
  return approval.updated_at
}

function ContractApprovals() {
  const { id: contractId, budgetId } = useParams()
  const navigate = useNavigate()
  usePageHeader({ title: null, hideTopHeader: true }, [])
  const isBudgetMode = Boolean(budgetId)
  const targetId = isBudgetMode ? budgetId : contractId
  const targetBasePath = isBudgetMode ? `/company/budgets/${budgetId}` : `/company/contracts/${contractId}`
  const targetApprovalsPath = `${targetBasePath}/approvals`
  const [contract, setContract] = useState(null)
  const [approvals, setApprovals] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [editModal, setEditModal] = useState(null)
  const [addModal, setAddModal] = useState(false)
  const [comment, setComment] = useState('')
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')

  const fetchContract = useCallback(async () => {
    const res = await api.get(targetBasePath)
    if (isBudgetMode) {
      const budget = res.data?.budget || null
      return budget
        ? {
            ...budget,
            budget_status: budget.approval_status,
          }
        : null
    }
    return res.data?.contract || null
  }, [isBudgetMode, targetBasePath])

  const fetchApprovals = useCallback(async () => {
    const res = await api.get(targetApprovalsPath)
    return res.data?.approvals || []
  }, [targetApprovalsPath])

  const fetchEmployees = useCallback(async () => {
    const res = await api.get('/company/employees?limit=500')
    return res.data?.employees || res.data || []
  }, [])

  const loadPage = useCallback(async () => {
    if (!targetId) return
    setLoading(true)
    try {
      const [nextContract, nextApprovals, nextEmployees] = await Promise.all([
        fetchContract(),
        fetchApprovals(),
        fetchEmployees(),
      ])
      setContract(nextContract)
      setApprovals(nextApprovals)
      setEmployees(nextEmployees)
    } catch (_) {
      setContract(null)
      setApprovals([])
      setEmployees([])
    } finally {
      setLoading(false)
    }
  }, [targetId, fetchApprovals, fetchContract, fetchEmployees])

  const reloadApprovals = useCallback(async () => {
    try {
      setApprovals(await fetchApprovals())
      setContract(await fetchContract())
    } catch (_) {
      setApprovals([])
    }
  }, [fetchApprovals, fetchContract])

  useEffect(() => {
    loadPage()
  }, [loadPage])

  const groupedApprovals = useMemo(() => splitApprovalsByStatus(approvals), [approvals])
  const summary = useMemo(
    () => buildApprovalSummary(approvals, contract?.budget_status),
    [approvals, contract?.budget_status],
  )

  const openEdit = (approval, newStatus) => {
    setEditModal({ approval, newStatus })
    setComment(approval?.comment || '')
  }

  const saveApprovalStatus = async () => {
    if (!editModal) return
    const { approval, newStatus } = editModal
    try {
      await api.patch(targetApprovalsPath, {
        employee_id: approval.employee_id || undefined,
        employee_name: approval.employee_name || undefined,
        status: newStatus,
        comment: newStatus === STATUS_REJECTED ? comment : undefined,
      })
      setEditModal(null)
      setComment('')
      await reloadApprovals()
      setMessage({ type: 'success', text: 'Статус обновлён' })
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось обновить' })
    }
  }

  const addApprover = async () => {
    if (!selectedEmployeeId) return
    try {
      await api.post(targetApprovalsPath, { employee_id: selectedEmployeeId })
      setAddModal(false)
      setSelectedEmployeeId('')
      await reloadApprovals()
      setMessage({ type: 'success', text: 'Согласователь добавлен' })
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось добавить' })
    }
  }

  const closeMessage = () => setMessage({ type: '', text: '' })

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  if (!contract) {
    return (
      <div className="company-page contract-approvals-page contract-approvals-page-v2">
        <p className="no-data">{isBudgetMode ? 'Бюджет не найден' : 'Договор не найден'}</p>
        <Button variant="ghost" className="btn-back" onClick={() => navigate(isBudgetMode ? '/company/budgets' : '/company/contracts')}>
          {isBudgetMode ? 'К списку бюджетов' : 'К списку договоров'}
        </Button>
      </div>
    )
  }

  const subtitle = isBudgetMode
    ? [contract.name, contract.contract_number, contract.client_name, contract.object_name].filter(Boolean).join(' · ')
    : contractSubtitle(contract)
  const breadcrumbItems = isBudgetMode
    ? [
        { label: 'Главная', to: '/' },
        { label: 'Бюджеты', to: '/company/budgets' },
        { label: contract.name || `Бюджет ${budgetId}`, to: `/company/budgets/${budgetId}` },
        { label: 'Согласование бюджета' },
      ]
    : [
        { label: 'Главная', to: '/' },
        { label: 'Договоры', to: '/company/contracts' },
        { label: contract.number || contractId, to: `/contract/${contractId}` },
        { label: 'Согласование бюджета' },
      ]
  const backLink = isBudgetMode ? `/company/budgets/${budgetId}` : `/contract/${contractId}`
  const backLabel = isBudgetMode ? 'К бюджету' : 'К договору'
  const availableEmployees = employees.filter(
    (emp) => !approvals.some((a) => a.employee_id === emp.id || a.employee_name === emp.full_name),
  )

  const renderCard = (approval) => {
    const meta = getApprovalStatusMeta(approval.status)
    return (
      <article key={approval.id || approval.employee_id || approval.employee_name} className={`approval-person-card approval-person-card--${meta.key}`}>
        <div className="approval-person-card__top">
          <div className="approval-person-avatar">{getApproverInitials(approval.employee_name)}</div>
          <div className="approval-person-main">
            <div className="approval-person-name">{approval.employee_name || '—'}</div>
            <div className="approval-person-date">{formatDate(statusDate(approval))}</div>
          </div>
          <span className={`approval-person-status approval-person-status--${meta.key}`}>{meta.label}</span>
        </div>
        {approval.comment ? (
          <div className="approval-person-comment">
            <span>Комментарий</span>
            {approval.comment}
          </div>
        ) : null}
        <div className="approval-person-actions">
          {meta.key === STATUS_PENDING ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => openEdit(approval, STATUS_APPROVED)}>Согласовать</Button>
              <Button variant="ghost" size="sm" onClick={() => openEdit(approval, STATUS_REJECTED)}>Отклонить</Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => openEdit(approval, STATUS_PENDING)}>Вернуть в ожидание</Button>
          )}
        </div>
      </article>
    )
  }

  const renderColumn = (statusKey) => {
    const meta = APPROVAL_STATUS_META[statusKey]
    const list = groupedApprovals[statusKey] || []
    return (
      <section className={`approval-status-column approval-status-column--${statusKey}`}>
        <div className="approval-status-column__head">
          <h2>{meta.title}</h2>
          <span>{list.length}</span>
        </div>
        <div className="approval-status-column__list">
          {list.length ? list.map(renderCard) : <p className="approval-status-column__empty">{meta.empty}</p>}
        </div>
      </section>
    )
  }

  return (
    <div className="company-page contract-approvals-page contract-approvals-page-v2">
      <header className="approval-screen-header">
        <Breadcrumb
          items={breadcrumbItems}
          className="approval-screen-breadcrumbs"
        />
        <div className="approval-screen-heading">
          <div className="approval-screen-title-row">
            <Button type="button" variant="ghost" className="approval-screen-back" onClick={() => navigate(backLink)} aria-label={backLabel}>
              <Icon name="chevronLeft" size={18} />
            </Button>
            <div>
              <h1 className="ds-page-title">Согласование бюджета</h1>
              {subtitle ? <div className="approval-screen-subtitle">{subtitle}</div> : null}
            </div>
            <StatusBadge variant={summary.budgetStatusVariant}>{summary.budgetStatusLabel}</StatusBadge>
          </div>
          <div className="approval-screen-actions">
            <Button variant="primary" onClick={() => setAddModal(true)}>
              <Icon name="plus" size={14} />
              Добавить согласователя
            </Button>
          </div>
        </div>
      </header>

      {message.text && (
        <div className={`message message-${message.type}`} role="alert">
          {message.text}
          <button type="button" className="message-close" onClick={closeMessage} aria-label="Закрыть">×</button>
        </div>
      )}

      <section className="approval-summary-panel ds-card">
        <div className="approval-summary-main">
          <div className="approval-summary-eyebrow">Прогресс согласования</div>
          <div className="approval-summary-value">{summary.approvedCount} из {summary.total}</div>
          <div className="approval-progress-bar" aria-label={`Согласовано ${summary.progressPercent}%`}>
            <span className="approval-progress-bar__approved" style={{ width: `${summary.progressPercent}%` }} />
          </div>
        </div>
        <div className="approval-summary-stats">
          <div>
            <span>{summary.progressPercent}%</span>
            <small>готово</small>
          </div>
          <div>
            <span>{summary.pendingCount}</span>
            <small>ожидают</small>
          </div>
          <div>
            <span>{summary.rejectedCount}</span>
            <small>отклонили</small>
          </div>
        </div>
      </section>

      <div className="approval-status-grid">
        {renderColumn(STATUS_PENDING)}
        {renderColumn(STATUS_APPROVED)}
        {renderColumn(STATUS_REJECTED)}
      </div>

      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal-content approval-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Изменить статус</h3>
            <p>{editModal.approval?.employee_name} → {getApprovalStatusMeta(editModal.newStatus).label}</p>
            {editModal.newStatus === STATUS_REJECTED && (
              <FormGroup label="Комментарий (причина отклонения)" className="contract-approvals-form-group">
                <textarea
                  className="ui-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Причина отклонения"
                  rows={3}
                  aria-label="Комментарий"
                />
              </FormGroup>
            )}
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setEditModal(null)}>Отмена</Button>
              <Button variant="primary" onClick={saveApprovalStatus}>Сохранить</Button>
            </div>
          </div>
        </div>
      )}

      {addModal && (
        <div className="modal-overlay" onClick={() => setAddModal(false)}>
          <div className="modal-content approval-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Добавить согласователя</h3>
            <FormGroup label="Сотрудник" className="contract-approvals-form-group">
              <Select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                style={{ width: '100%' }}
                aria-label="Сотрудник"
              >
                <option value="">Выберите сотрудника</option>
                {availableEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.full_name || emp.id}</option>
                ))}
              </Select>
            </FormGroup>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setAddModal(false)}>Отмена</Button>
              <Button variant="primary" onClick={addApprover} disabled={!selectedEmployeeId}>Добавить</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContractApprovals
